'use client';

import React from 'react';
import type { ExtendedBiometricCaptureState } from '../hooks/useStableBiometricCaptureState';

interface FaceMeshOverlayProps {
  /** Whether a face is detected */
  detected: boolean;
  /** Whether all quality metrics are met */
  ready: boolean;
  /** Optional canonical capture state so the overlay can stay in sync with the status copy */
  state?: ExtendedBiometricCaptureState;
  /** Hold-still countdown pulse — increments once per flash while the face is
   *  held ready. Each increment replays a one-shot flash on the guide marker. */
  flashPulse?: number;
  /** Width of the overlay container */
  width?: number;
  /** Height of the overlay container */
  height?: number;
  /** Scale factor for mobile vs desktop */
  scale?: number;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * Simple face cutout guide overlay inspired by modern banking apps (Capitec-style)
 * Clean, minimal face-shaped guide with clear positioning instructions
 */
export default function FaceMeshOverlay({
  detected,
  ready,
  state,
  flashPulse = 0,
  width = 200,
  height = 280,
  scale = 1,
  className = ''
}: FaceMeshOverlayProps) {
  const resolvedState: ExtendedBiometricCaptureState = state ?? (ready ? 'ready' : detected ? 'detected' : 'searching');

  // Modern banking app palette:
  // - slate while searching
  // - blue/cyan while tracking  
  // - green when ready
  const isWarningState = resolvedState === 'move_closer'
    || resolvedState === 'move_back'
    || resolvedState === 'improve_lighting'
    || resolvedState === 'resetting';
  const isTrackingState = resolvedState === 'detected' || resolvedState === 'hold_still';
  
  const primaryColor = resolvedState === 'ready'
    ? '#10b981' // emerald-500
    : isWarningState
      ? '#f59e0b' // amber-500
      : isTrackingState
        ? '#3b82f6' // blue-500
        : '#94a3b8'; // slate-400
  
  const secondaryColor = resolvedState === 'ready'
    ? 'rgba(16, 185, 129, 0.2)'
    : isWarningState
      ? 'rgba(245, 158, 11, 0.15)'
      : isTrackingState
        ? 'rgba(59, 130, 246, 0.15)'
        : 'rgba(148, 163, 184, 0.1)';
  
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;
  
  // Center points
  const cx = scaledWidth / 2;
  const cy = scaledHeight / 2;
  
  // Face cutout dimensions (oval shape like Capitec)
  const faceWidth = scaledWidth * 0.65;
  const faceHeight = scaledHeight * 0.75;
  
  // Corner guide markers
  const cornerSize = 12 * scale;
  const cornerOffset = 8 * scale;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${scaledWidth} ${scaledHeight}`}
      preserveAspectRatio="xMidYMid slice"
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {/* Solid background with face cutout using path and evenodd fill rule */}
      <path
        d={`
          M 0,0 
          L ${scaledWidth},0 
          L ${scaledWidth},${scaledHeight} 
          L 0,${scaledHeight} 
          Z
          M ${cx},${cy}
          m ${-faceWidth / 2},0
          a ${faceWidth / 2},${faceHeight / 2} 0 1,0 ${faceWidth},0
          a ${faceWidth / 2},${faceHeight / 2} 0 1,0 -${faceWidth},0
          Z
        `}
        fill="rgba(0, 0, 0, 0.85)"
        fillRule="evenodd"
      />
      
      {/* Guide marker group — keyed by the hold-still flash pulse so each
          pulse remounts the group and replays the one-shot flash. */}
      <g key={flashPulse > 0 ? `hold-flash-${flashPulse}` : 'hold-idle'}>
        {flashPulse > 0 && (
          <animate
            attributeName="opacity"
            values="1;0.15;1"
            dur="0.45s"
            repeatCount="1"
          />
        )}

        {/* Face cutout border */}
        <ellipse
          cx={cx}
          cy={cy}
          rx={faceWidth / 2}
          ry={faceHeight / 2}
          fill="none"
          stroke={primaryColor}
          strokeWidth={3 * scale}
          strokeDasharray={ready ? '0' : `${8 * scale},${4 * scale}`}
          opacity={detected ? 1 : 0.7}
        />

        {/* Inner face guide */}
        <ellipse
          cx={cx}
          cy={cy}
          rx={faceWidth * 0.35}
          ry={faceHeight * 0.4}
          fill="none"
          stroke={primaryColor}
          strokeWidth={1.5 * scale}
          strokeDasharray={`${4 * scale},${4 * scale}`}
          opacity={detected ? 0.6 : 0.3}
        />

        {/* Corner guide markers */}
        {[
          // Top-left
          { x: cx - faceWidth / 2 - cornerOffset, y: cy - faceHeight / 2 - cornerOffset },
          // Top-right
          { x: cx + faceWidth / 2 + cornerOffset, y: cy - faceHeight / 2 - cornerOffset },
          // Bottom-left
          { x: cx - faceWidth / 2 - cornerOffset, y: cy + faceHeight / 2 + cornerOffset },
          // Bottom-right
          { x: cx + faceWidth / 2 + cornerOffset, y: cy + faceHeight / 2 + cornerOffset },
        ].map((corner, i) => (
          <g key={i}>
            {/* Horizontal line */}
            <line
              x1={corner.x - cornerSize}
              y1={corner.y}
              x2={corner.x + cornerSize}
              y2={corner.y}
              stroke={primaryColor}
              strokeWidth={2 * scale}
              strokeLinecap="round"
            />
            {/* Vertical line */}
            <line
              x1={corner.x}
              y1={corner.y - cornerSize}
              x2={corner.x}
              y2={corner.y + cornerSize}
              stroke={primaryColor}
              strokeWidth={2 * scale}
              strokeLinecap="round"
            />
          </g>
        ))}
      </g>
      
      {/* Center crosshair */}
      <g opacity={detected ? 0.3 : 0.15}>
        <line
          x1={cx - 20 * scale}
          y1={cy}
          x2={cx + 20 * scale}
          y2={cy}
          stroke={primaryColor}
          strokeWidth={1 * scale}
        />
        <line
          x1={cx}
          y1={cy - 20 * scale}
          x2={cx}
          y2={cy + 20 * scale}
          stroke={primaryColor}
          strokeWidth={1 * scale}
        />
      </g>
      
      {/* Animated scanning line when ready */}
      {ready && (
        <line
          x1={cx - faceWidth / 2}
          y1={cy}
          x2={cx + faceWidth / 2}
          y2={cy}
          stroke={primaryColor}
          strokeWidth={2 * scale}
          opacity={0.8}
        >
          <animate
            attributeName="y1"
            values={`${cy - faceHeight / 2};${cy + faceHeight / 2};${cy - faceHeight / 2}`}
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="y2"
            values={`${cy - faceHeight / 2};${cy + faceHeight / 2};${cy - faceHeight / 2}`}
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.8;0.3;0.8"
            dur="2s"
            repeatCount="indefinite"
          />
        </line>
      )}
      
      {/* Status indicator dots */}
      <g transform={`translate(${cx - 30 * scale}, ${scaledHeight - 20 * scale})`}>
        <circle
          cx={0}
          cy={0}
          r={4 * scale}
          fill={resolvedState === 'searching' ? primaryColor : 'rgba(148, 163, 184, 0.3)'}
        />
        <circle
          cx={15 * scale}
          cy={0}
          r={4 * scale}
          fill={isTrackingState ? primaryColor : 'rgba(148, 163, 184, 0.3)'}
        />
        <circle
          cx={30 * scale}
          cy={0}
          r={4 * scale}
          fill={ready ? primaryColor : 'rgba(148, 163, 184, 0.3)'}
        />
      </g>
    </svg>
  );
}